import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useMetaPixel } from "@/hooks/useMetaPixel";
import { phCapture } from "@/lib/posthog-events";
import { trackGoogleSignup } from "@/lib/google-ads";

// Fires the signup conversions (PostHog `signup_completed`, Meta
// CompleteRegistration, Google Ads signup) for OAuth (Google) signups.
//
// Why this exists: the email/OTP path fires these inline in SignIn
// (handleVerifyOtp), but the Google handlers — native one-tap and the redirect
// fallback — navigate straight to the dashboard without firing anything, and
// there is no OAuth callback route. So every Google signup was invisible to
// Google Ads, Meta, and the PostHog funnel (only the founder Telegram/Slack
// ping fired, via the auth.users trigger). Mounting this app-wide catches the
// session however it arrives, including the redirect return.
//
// Fire-once + no double-count:
//   * provider === "email" → skip. The OTP path already tracks itself; skipping
//     here avoids a double-fire race between the two.
//   * `ad_signup_dedup_token` RPC is the authoritative once-per-user guard: it
//     mints an opaque HMAC token only on the first call per user (null
//     thereafter), and pre-existing users were backfilled as already-claimed.
//     It doubles as the Google Ads dedup transaction id without ever exposing
//     the raw user id.
//   * The recency guard is belt-and-suspenders: OAuth users who signed up
//     before this shipped never claimed a token, so without it their next login
//     would mint one and fire a stale conversion. A genuine new signup reaches
//     this within seconds of account creation.
const FRESH_WINDOW_MS = 60 * 60 * 1000;

export default function SignupTracker() {
  const { user } = useAuth();
  const { trackCompleteRegistration } = useMetaPixel();
  const firedRef = useRef(false);

  useEffect(() => {
    if (!user || firedRef.current) return;

    // Email/OTP signups are tracked inline in SignIn — partition on provider so
    // the two paths never both fire for one account.
    const provider = user.app_metadata?.provider;
    if (provider === "email") return;

    // Only genuinely-new accounts (see FRESH_WINDOW_MS note above).
    const createdMs = user.created_at ? Date.parse(user.created_at) : NaN;
    if (!Number.isFinite(createdMs) || Date.now() - createdMs > FRESH_WINDOW_MS) return;

    // Guard against re-entry within this session; the RPC is the cross-session
    // fire-once source of truth.
    firedRef.current = true;
    void (async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RPC newer than generated types (same cast SignIn/menu-created use)
        const { data: token } = await supabase.rpc("ad_signup_dedup_token" as any);
        if (typeof token !== "string" || !token) return; // already claimed / key unprovisioned → fire nothing
        const email = user.email ?? undefined;
        phCapture("signup_completed", { method: provider ?? "oauth" });
        void trackCompleteRegistration({ email });
        trackGoogleSignup({ email }, token);
      } catch {
        // best-effort — a tracking failure must never disrupt the app
      }
    })();
  }, [user, trackCompleteRegistration]);

  return null;
}
